/**
 * Platform-aware icon for native screens.
 * iOS: SF Symbol (react-native-sfsymbols).
 * Android: Lucide icon fallback so we never load iOS-only SFSymbol on Android.
 * Keeps a single .native.tsx screen set; no duplicate iOS/Android screen files.
 */

import React from 'react';
import { Platform, StyleProp, View, ViewStyle } from 'react-native';
import { useTheme } from '../../theme';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import {
  User,
  Sun,
  Smartphone,
  Heart,
  Bell,
  Paintbrush,
  MessageCircle,
  Shield,
  ShieldCheck,
  ShieldPlus,
  Goal,
  TrendingUp,
  TrendingDown,
  Search,
  FileText,
  BookOpen,
  Book,
  Watch,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Dumbbell,
  Clock,
  Flame,
  Play,
  Pause,
  Square,
  CheckCircle,
  CheckCircle2,
  Circle,
  Check,
  FileSearch,
  MapPin,
  Zap,
  Lightbulb,
  Calendar,
  ClipboardList,
  Columns2,
  Paperclip,
  SquarePen,
  Moon,
  Target,
  Trophy,
  BarChart3,
  Brain,
  UtensilsCrossed,
  Mail,
  Activity,
  Wind,
  Ruler,
  Mountain,
  RotateCcw,
  RotateCw,
  Plus,
  PersonStanding,
  Snowflake,
  Minus,
  XCircle,
  X,
  AlertTriangle,
  AlertCircle,
  Wrench,
  Info,
  List,
  BookMarked,
  ExternalLink,
  Quote,
  BedDouble,
  Pencil,
  Leaf,
  Hash,
  HandMetal,
  Cloud,
  CloudDrizzle,
  CloudRain,
  CloudLightning,
  CloudFog,
  CloudSun,
  CloudSunRain,
  CloudMoon,
  Haze,
  LineChart,
  CalendarDays,
  Star,
  Flag,
  Sparkles,
  Gauge,
  ArrowUpDown,
  ArrowUp,
  Footprints,
  Route,
  Timer,
  BatteryCharging,
  Wifi,
  WifiOff,
  Ban,
  Megaphone,
  Gift,
  Ticket,
  Users,
  Share2,
  Volume2,
  VolumeX,
} from 'lucide-react-native';

// Only load on iOS to avoid Android crash (react-native-sfsymbols is iOS-only)
const SFSymbol = Platform.OS === 'ios' ? require('react-native-sfsymbols').SFSymbol : null;

/** SF Symbol name → Lucide component for Android fallback */
const SF_TO_LUCIDE: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
  'person.crop.circle.fill': User,
  'sun.horizon.fill': Sun,
  iphone: Smartphone,
  'bolt.heart.fill': Zap,
  'bell.fill': Bell,
  // The in-timer voice toggle. It shipped passing Feather names
  // ('volume-2'/'volume-x') straight to the SF Symbol component, which
  // renders NOTHING for an unknown symbol — so the control was invisible
  // on iOS and the athlete had no way to see or verify mute state.
  'speaker.wave.2.fill': Volume2,
  'speaker.slash.fill': VolumeX,
  'paintbrush.fill': Paintbrush,
  'text.bubble.fill': MessageCircle,
  'lock.shield.fill': Shield,
  'chart.line.uptrend.xyaxis.circle': TrendingUp,
  'chart.line.uptrend.xyaxis': LineChart,
  magnifyingglass: Search,
  'doc.text.fill': FileText,
  'doc.text.magnifyingglass': FileSearch,
  'books.vertical.fill': BookOpen,
  'book.fill': Book,
  'message.fill': MessageCircle,
  applewatch: Watch,
  // Wear OS / Pixel Watch / Galaxy Watch — Android smartwatch peer to
  // applewatch. Maps to the same Lucide Watch glyph (the source/context
  // around the badge distinguishes platform). SF Symbol fallback on iOS is
  // 'applewatch' since there's no Wear-OS-specific symbol and a Wear OS
  // workout never renders on an iOS build in practice.
  wearos: Watch,
  'suit.heart.fill': Heart,
  'chevron.left': ChevronLeft,
  'chevron.right': ChevronRight,
  'chevron.up': ChevronUp,
  'chevron.down': ChevronDown,
  'dumbbell.fill': Dumbbell,
  'clock.fill': Clock,
  clock: Clock,
  'flame.fill': Flame,
  // PostCompletionMetricsGrid icons that were falling back to BookOpen on
  // Android (Build 180 surfaced this — Recovery / Duration / Distance tiles).
  // 'speedometer' is also mapped in the M35 Activity Timeline section
  // below — leaving that one as the canonical entry to keep the M35
  // grouping intact and avoid TS1117 duplicate-key error.
  'gauge.medium': Gauge,
  'point.topleft.down.to.point.bottomright.curvepath': Route,
  'play.fill': Play,
  'pause.fill': Pause,
  'stop.fill': Square,
  'checkmark.circle.fill': CheckCircle,
  'checkmark.circle': CheckCircle2,
  circle: Circle,
  checkmark: Check,
  'location.fill': MapPin,
  'heart.fill': Heart,
  paperclip: Paperclip,
  'moon.fill': Moon,
  'battery.100.bolt': BatteryCharging,
  target: Target,
  'trophy.fill': Trophy,
  'chart.bar.fill': BarChart3,
  'brain.head.profile': Brain,
  'fork.knife': UtensilsCrossed,
  'envelope.fill': Mail,
  'shield.fill': Shield,
  'doc.on.clipboard.fill': ClipboardList,
  'bolt.fill': Zap,
  calendar: Calendar,
  'lightbulb.fill': Lightbulb,
  'waveform.path.ecg': Activity,
  activity: Activity,

  // ── Close / Dismiss ──
  'xmark.circle.fill': XCircle,
  'xmark.circle': XCircle,
  x: X,

  // ── Alerts / Info ──
  'exclamationmark.triangle': AlertTriangle,
  'exclamationmark.triangle.fill': AlertTriangle,
  'exclamationmark.circle': AlertCircle,
  'exclamationmark.circle.fill': AlertCircle,
  'alert-circle': AlertCircle,
  'info.circle': Info,
  'info.circle.fill': Info,
  // Missed-workout indicator: SF Symbol `nosign` (Apple's official
  // prohibited circle-with-slash) on iOS, Lucide `Ban` on Android.
  // Used by SimpleWorkoutCard to replace the chevron when a workout
  // is missed — same screen position, opposite affordance.
  nosign: Ban,

  // ── Connectivity ──
  'wifi': Wifi,
  'wifi.slash': WifiOff,

  // ── Tools / Misc ──
  'wrench.fill': Wrench,
  'list.bullet.rectangle': List,
  // The Past weeks view toggle (Past-weeks calendar, 2026-09): List | 2 months | 1 month.
  'list.bullet': List,
  'rectangle.split.2x1': Columns2,
  // Per-exercise weight & rep logging sprint (2026-05) — Log trigger button
  // on the native timer header. ClipboardList visually distinguishes the
  // "log what you did" action from a plain list bullet.
  'list.bullet.clipboard': ClipboardList,
  'book.closed.fill': BookMarked,
  'book.pages.fill': BookOpen,
  'arrow.up.right.square': ExternalLink,
  'square.and.arrow.up': Share2,
  'text.quote': Quote,
  'bed.double.fill': BedDouble,
  'bubble.left.fill': MessageCircle,
  heart: Heart,
  'chevron-down': ChevronDown,
  'chevron-right': ChevronRight,
  'pencil': Pencil,
  // iOS renders the real SF Symbol; Android needs the matching Lucide
  // glyph. This previously fell back to bare `Pencil`, so Android showed
  // a different icon than iOS for the same name.
  'square.and.pencil': SquarePen,

  // ── Workout / Mobility SF Symbols (non-fitness fallbacks only) ──
  'figure.strengthtraining.traditional': Dumbbell,
  'figure.highintensity.intervaltraining': Zap,
  'figure.climbing': Mountain,
  'figure.snowboarding': Snowflake,
  'figure.cross.training': Dumbbell,
  'figure.cooldown': Activity,       // Active recovery rest day
  'moon.zzz.fill': Moon,             // Complete rest / sleep day
  'figure.mind.and.body': Brain,
  'figure.yoga': Leaf,
  'circle.grid.cross.fill': Target,
  'arrow.triangle.2.circlepath': RotateCcw,
  // Cycling-cadence glyph for the in-timer sensor indicator. SF
  // 'arrow.clockwise' on iOS; lucide RotateCw on Android (matches the
  // pre-sprint SensorStatusBar's cadence icon exactly).
  'arrow.clockwise': RotateCw,
  'cross.case.fill': Plus,
  'cross.fill': ShieldPlus,
  'snowflake': Snowflake,
  // ── Metric pill icons (emoji→icon swap, March 13) ──
  ruler: Ruler,
  'mountain.2.fill': Mountain,
  wind: Wind,

  // ── Activity Timeline + Metrics Grid icons (Sprint M35) ──
  speedometer: Gauge,
  'metronome.fill': Timer,
  'shoe.fill': Footprints,
  'arrow.up.arrow.down': ArrowUpDown,
  // Plain directional arrow. Used rotated as the hourly strip's wind vector
  // (see weatherGranularity.windArrowRotation). MUST stay mapped: on Android an
  // unmapped SF name falls through to the BookOpen fallback below, which would
  // render a book where the wind direction should be.
  'arrow.up': ArrowUp,

  // ── Additional icons (emoji→icon swap, March 14) ──
  'leaf.fill': Leaf,
  'lungs.fill': Wind,
  'hand.tap.fill': HandMetal,
  'number.square.fill': Hash,
  'mappin.and.ellipse': MapPin,
  'star.fill': Star,
  sparkles: Sparkles,
  'flag.fill': Flag,

  // ── Campaign glyphs (CAMPAIGN_SF_MAP in campaignIcons) ──
  // applewatch / moon.fill / flame.fill / star.fill / clock.fill /
  // checkmark.circle.fill / info.circle.fill / bell.fill all already
  // mapped above. These two were the only campaign SF symbols missing —
  // without them the announcement/contextual megaphone + gift fell back
  // to BookOpen on Android.
  'megaphone.fill': Megaphone,
  'gift.fill': Gift,
  'ticket.fill': Ticket,   // 🌞 Summer50: Redeem-code settings tile
  'figure.2': Users,       // 🌞 Summer50: Refer-a-friend settings tile

  // ── Momentum direction arrows ──
  'arrow.up.right': TrendingUp,
  'arrow.down.right': TrendingDown,
  'minus': Minus,

  // ── Insights metric widgets ──
  'shield.checkered': ShieldCheck,
  'gauge.open.with.lines.needle.33percent': Goal,
  // (duplicates removed — mapped above in main block)

  // ── Weather SF Symbols ──
  'sun.max.fill': Sun,
  'cloud.sun.fill': CloudSun,
  'cloud.moon.fill': CloudMoon,
  'cloud.fill': Cloud,
  'smoke.fill': Haze,
  'cloud.drizzle.fill': CloudDrizzle,
  'cloud.sun.rain.fill': CloudSunRain,
  'cloud.rain.fill': CloudRain,
  'cloud.bolt.fill': CloudLightning,
  'cloud.fog.fill': CloudFog,
};

/** SF Symbol → MaterialCommunityIcons name for fitness icons on Android */
const SF_TO_MATERIAL: Record<string, string> = {
  'figure.run': 'run',
  'figure.outdoor.cycle': 'bike',
  'figure.indoor.cycle': 'bike',
  'figure.pool.swim': 'swim',
  'figure.rower': 'rowing',
  'figure.walk': 'walk',
  'figure.yoga': 'yoga',
  'figure.flexibility': 'human-handsup',
  'figure.core.training': 'human',
  'figure.jumprope': 'jump-rope',
  'figure.mixed.cardio': 'run-fast',
  'figure.martial.arts': 'karate',
  'figure.tennis': 'tennis',
  'figure.golf': 'golf',
  // Neutral team-sports glyph. iOS `sportscourt.fill` is a generic court;
  // the Android peer must NOT be a specific ball (was 'basketball' pre-2026-07-20,
  // which read as basketball for hockey/soccer/etc. and clashed with the iOS
  // court). 'whistle' reads as "team sport" without picking a sport.
  'sportscourt.fill': 'whistle',
  'bolt.heart.fill': 'heart-flash',
  'battery.100.bolt': 'battery-charging-100',
  'dumbbell.fill': 'dumbbell',
  'figure.strengthtraining.traditional': 'dumbbell',
  'figure.highintensity.intervaltraining': 'heart-flash',
  'figure.climbing': 'carabiner',
  'figure.snowboarding': 'snowboard',
  'figure.cross.training': 'dumbbell',
  'figure.cooldown': 'human-handsdown',   // Active recovery rest day
  'moon.zzz.fill': 'weather-night',       // Complete rest / sleep day
  'book.and.wrench': 'book-search',       // Technical Depth slider — iOS SF Symbol pairs a book with a wrench; closest MCI is book-search
  'mic.fill': 'microphone',               // Voice completion mic (fluent-completion Sprint 2, F1)
};

export interface NativeIconProps {
  name: string;
  size?: number;
  color: string;
  weight?: 'ultralight' | 'thin' | 'light' | 'regular' | 'medium' | 'semibold' | 'bold' | 'heavy' | 'black';
  style?: StyleProp<ViewStyle>;
}

// Throttle state for the null-name dev warn. Module-level so all
// NativeIcon instances share the same throttle window. Without this,
// a re-rendering component passing `name=undefined` 100×/sec would
// spam the dev Metro log unusably hard. 5 second window is enough to
// surface the bug on first occurrence without flooding.
let lastNullNameWarnAt = 0;
const NULL_NAME_WARN_THROTTLE_MS = 5000;

export function NativeIcon({ name, size = 24, color, weight = 'regular', style }: NativeIconProps) {
  const { palette } = useTheme();
  const ink = color ?? palette.ink.primary;
  // Defensive null guard. TypeScript declares `name: string` as
  // required, but at runtime an icon-map lookup miss (e.g.
  // SF_TO_LUCIDE[someUnmappedType] → undefined) can pass `undefined`
  // through. On iOS this reaches Apple's CoreUI framework and logs
  //   [CoreUI] CUICatalog: Invalid asset name supplied: '(null)'
  // per render — spams hundreds of times during the workout timer
  // because icons re-render every tick. Falling back to the same
  // BookOpen icon used downstream when SF_TO_LUCIDE misses keeps
  // the component renderable without polluting native logs.
  //
  // In __DEV__ we additionally console.warn (throttled to once per
  // 5s via module-level lastNullNameWarnAt) so developers can find
  // the upstream caller via the JS stack trace. Production stays
  // silent — this is diagnostic noise, not a user-visible bug.
  if (!name) {
    if (__DEV__) {
      const now = Date.now();
      if (now - lastNullNameWarnAt > NULL_NAME_WARN_THROTTLE_MS) {
        lastNullNameWarnAt = now;
        // eslint-disable-next-line no-console
        console.warn('[NativeIcon] received null/undefined name — rendering BookOpen fallback. Trace the caller and pass a valid icon name.');
      }
    }
    const fallback = <BookOpen size={size} color={ink} />;
    if (style) return <View style={style}>{fallback}</View>;
    return fallback;
  }

  if (Platform.OS === 'ios' && SFSymbol) {
    return <SFSymbol name={name} size={size} color={ink} weight={weight} style={style} />;
  }

  // Android: prefer MaterialCommunityIcons for fitness icons (much better than Lucide Activity)
  const materialName = SF_TO_MATERIAL[name];
  if (materialName) {
    const iconEl = <MaterialCommunityIcons name={materialName as any} size={size} color={ink} />;
    if (style) return <View style={style}>{iconEl}</View>;
    return iconEl;
  }

  // Handle dynamic SF Symbol patterns (e.g. "9.square.fill" → CalendarDays)
  let LucideIcon = SF_TO_LUCIDE[name];
  if (!LucideIcon && /^\d+\.square(\.fill)?$/.test(name)) {
    LucideIcon = CalendarDays;
  }
  const iconEl = !LucideIcon
    ? <BookOpen size={size} color={ink} />
    : <LucideIcon size={size} color={ink} />;
  if (style) {
    return <View style={style}>{iconEl}</View>;
  }
  return iconEl;
}
